export type PointRuleActionType =
  | "checkin"
  | "onboarding"
  | "share_app"
  | "share_event"
  | "suggestion_implemented"
  | "suggestion_submitted"
  | "referral"
  | "event_bonus"
  | "streak"
  | "no_show";

export type PointRuleKind = "atomic" | "entity_amount" | "threshold_window" | "streak";

export type PointWindowPeriod = "week" | "month" | "custom";

export type PointRuleEditableField =
  | "pointsValue"
  | "active"
  | "visibleToUser"
  | "displayTitle"
  | "displayDescription"
  | "iconKey"
  | "windowPeriod"
  | "windowStartsOn"
  | "windowEndsOn"
  | "thresholdCount"
  | "streakPeriods"
  | "freeEventsOnly"
  | "eventTypeId"
  | "eventSubTypeId";

export interface PointRule {
  id: number;
  tenantId: string;
  actionType: PointRuleActionType;
  ruleKind: PointRuleKind;
  baseRuleId: number | null;
  /** Magnitud positiva también en no_show: el signo lo pone isPenalty, no el valor. null = sin configurar. */
  pointsValue: number | null;
  windowPeriod: PointWindowPeriod | null;
  /** YYYY-MM-DD, día calendario en hora AR. Solo con windowPeriod "custom"; inclusive. */
  windowStartsOn: string | null;
  windowEndsOn: string | null;
  thresholdCount: number;
  /** 0 = paga cada ventana consecutiva a partir de la segunda. */
  streakPeriods: number;
  freeEventsOnly: boolean;
  eventTypeId: number | null;
  eventSubTypeId: number | null;
  displayTitle: string | null;
  displayDescription: string | null;
  iconKey: string | null;
  visibleToUser: boolean;
  active: boolean;
  effectiveFrom: string;
  updatedAt: string;
  /** Solo no_show: la pantalla muestra el signo "−". */
  isPenalty: boolean;
  /** true → los campos estructurales quedan congelados para todos. */
  hasAwardedPoints: boolean;
  /** Fuente de verdad de qué puede editar el usuario: no reimplementar la matriz de roles. */
  editableFields: PointRuleEditableField[];
  /**
   * Otra regla activa de la misma acción que se apila con ésta: las dos cobran un mismo hecho.
   * Se muestra como aviso y no bloquea nada, porque apilar también puede ser deliberado.
   * El criterio lo decide el backend; acá sólo se renderiza.
   *
   * Sólo viene con valor en el listado. Tras un alta o edición llega `null`, pero la mutación
   * invalida la cache y el listado se vuelve a pedir, así que el aviso queda al día igual.
   */
  stackedWithRuleId: number | null;
}

/** Solo los campos que cambian. Cada key presente (≠ undefined) viaja al PUT. */
export type PointRuleChanges = Partial<Pick<PointRule, PointRuleEditableField>>;

export interface UpdatePointRulePayload {
  id: number;
  tenantId: string; // solo para invalidar cache / no viaja en el body
  changes: PointRuleChanges;
}

export interface CreatePointRulePayload extends PointRuleChanges {
  tenantId: string;
  actionType: PointRuleActionType;
  ruleKind: PointRuleKind;
  baseRuleId?: number | null;
}

/**
 * Baja lógica: la regla deja de dar puntos y de listarse, pero sigue en la base porque las
 * filas del ledger que ya acreditó la siguen apuntando y se siguen explicando.
 */
export interface DeactivatePointRulePayload {
  id: number;
  tenantId: string; // solo para invalidar cache / no viaja en el body
}

/** Un hito de la racha semanal: se paga al llegar a sus semanas seguidas. */
export interface PointStreakMilestone {
  id: number;
  streakPeriods: number;
  pointsValue: number | null;
  active: boolean;
  hasAwardedPoints: boolean;
}

/**
 * La racha del tenant: hitos en N, 2N, 3N… semanas seguidas. intervalWeeks null = sin configurar.
 * Título, descripción e ícono son de toda la racha; cada hito agrega sus semanas al título.
 */
export interface PointStreakConfig {
  intervalWeeks: number | null;
  displayTitle: string;
  displayDescription: string | null;
  iconKey: string | null;
  visibleToUser: boolean;
  milestones: PointStreakMilestone[];
}

export interface SavePointStreakPayload {
  tenantId: string;
  intervalWeeks: number;
  displayTitle: string;
  displayDescription: string | null;
  iconKey: string | null;
  visibleToUser: boolean;
  milestones: { pointsValue: number; active: boolean }[];
}

export const MIN_POINT_RULE_VALUE = 1;
export const MAX_POINT_RULE_VALUE = 10000;


/**
 * Acciones que se pueden configurar pero todavía nada acredita (la app no las registra). El
 * backend no deja activarlas; acá se muestran como "Próximamente".
 *
 * `onboarding` SALIÓ DE LA LISTA: desde que el `POST /mobile/onboarding/complete` acredita,
 * el backend deja activarla y mostrarla como "Próximamente" sería mentir. Esta lista es el
 * espejo de `POINT_ACTIONS_WITHOUT_ACCRUAL` del worker: si se desincronizan, el Backoffice
 * ofrece un switch que el backend rechaza, o esconde uno que sí anda.
 *
 * `streak` TAMPOCO VA ACÁ, aunque el BO la muestre como "Próximamente": el motor sí la
 * acredita (el paso "streaks" de `awardAttendancePointsUseCase`) y nunca estuvo en la lista
 * del worker. Lo que la frena es la decisión del BO de no lanzarla todavía, que es
 * `POINT_RULE_ACTIONS_COMING_SOON`. Mezclarlas acá rompía el espejo.
 */
export const POINT_RULE_ACTIONS_WITHOUT_ACCRUAL: readonly PointRuleActionType[] = [
  "share_app",
  "share_event",
  "suggestion_implemented",
];

export const isPointRuleActionWithoutAccrual = (actionType: PointRuleActionType): boolean =>
  POINT_RULE_ACTIONS_WITHOUT_ACCRUAL.includes(actionType);

/**
 * Acciones terminadas que se lanzan más adelante: ni siquiera se configuran. En el alta salen
 * grisadas y sus filas no se editan. El backend sí las aceptaría, así que las frena el BO.
 */
export const POINT_RULE_ACTIONS_COMING_SOON: readonly PointRuleActionType[] = [
  "streak",
  "suggestion_implemented",
];

export const isPointRuleActionComingSoon = (actionType: PointRuleActionType): boolean =>
  POINT_RULE_ACTIONS_COMING_SOON.includes(actionType);

/**
 * Lo que el listado marca como "Próximamente" y no deja prender: o el motor todavía no la
 * acredita, o el BO decidió no lanzarla. Son dos motivos distintos —y por eso dos listas— con
 * la misma consecuencia para el usuario, así que el badge y el switch preguntan por acá.
 */
export const isPointRuleActionUpcoming = (actionType: PointRuleActionType): boolean =>
  isPointRuleActionWithoutAccrual(actionType) || isPointRuleActionComingSoon(actionType);

/**
 * Una regla de umbral que no es de Asistencia: la base la acepta (la 0022 del worker habilitó
 * "una sugerencia por semana"), pero el BO solo ofrece el umbral para Asistencia. Las que ya
 * existen se ven como "Próximamente" y, igual que la racha, no se activan, no se editan ni se
 * dan de baja desde acá.
 */
export const isPointRuleComingSoon = (rule: Pick<PointRule, "actionType" | "ruleKind">): boolean =>
  isPointRuleActionComingSoon(rule.actionType) ||
  (rule.ruleKind === "threshold_window" && rule.actionType !== "checkin");

/**
 * Acciones retiradas: el BO no las ofrece en el alta ni en el filtro, y sus reglas existentes no se
 * listan. Compartir app quedó reemplazada por "Invitá a un amigo" (`referral`), que sí atribuye la invitación.
 */
export const POINT_RULE_ACTIONS_RETIRED: readonly PointRuleActionType[] = ["share_app"];

export const isPointRuleActionRetired = (actionType: PointRuleActionType): boolean =>
  POINT_RULE_ACTIONS_RETIRED.includes(actionType);

/**
 * Acciones que la app NO dibuja en "Cómo ganar": el bonus por evento se acredita al confirmar
 * la asistencia, no es una forma de ganar que el usuario elija de una lista, así que no tiene
 * fila. La vista previa se apaga para estas reglas en vez de mostrar algo que no existe.
 */
export const POINT_RULE_ACTIONS_WITHOUT_APP_PREVIEW: readonly PointRuleActionType[] = ["event_bonus"];

export const isPointRuleActionWithoutAppPreview = (actionType: PointRuleActionType): boolean =>
  POINT_RULE_ACTIONS_WITHOUT_APP_PREVIEW.includes(actionType);

export const POINT_RULE_ACTION_LABELS: Record<PointRuleActionType, string> = {
  checkin: "Asistencia a evento",
  onboarding: "Onboarding",
  share_app: "Compartir app",
  share_event: "Compartir evento",
  suggestion_implemented: "Sugerencia implementada",
  suggestion_submitted: "Sugerencia enviada",
  referral: "Invitación a la app",
  event_bonus: "Bonus por evento",
  streak: "Hito de racha",
  no_show: "Inasistencia",
};

/** Sin título propio, la regla se nombra por su acción. */
export const getPointRuleDisplayName = (rule: PointRule): string =>
  rule.displayTitle ?? POINT_RULE_ACTION_LABELS[rule.actionType];

export const POINT_RULE_KIND_LABELS: Record<PointRuleKind, string> = {
  atomic: "Por acción",
  entity_amount: "Según el evento",
  threshold_window: "Umbral por período",
  streak: "Racha",
};

export const POINT_WINDOW_PERIOD_LABELS: Record<PointWindowPeriod, string> = {
  week: "Semana",
  month: "Mes",
  custom: "A medida",
};

/** Mismo tope que el backend para el período a medida: "hasta" ≤ "desde" + 6 meses. */
export const POINT_CUSTOM_WINDOW_MAX_MONTHS = 6;

/** "2026-10-01" → "01/10/2026", como lo muestran los calendarios del modal. */
const formatDayKey = (dayKey: string): string => {
  const [year, month, day] = dayKey.split("-");
  return year && month && day ? `${day}/${month}/${year}` : dayKey;
};

/** Un período a medida cuyo "hasta" ya pasó: deja de pagar sola, sin apagarse. */
export const isPointRuleWindowFinished = (rule: PointRule, todayDayKey: string): boolean =>
  rule.windowPeriod === "custom" && rule.windowEndsOn !== null && rule.windowEndsOn < todayDayKey;

export const POINT_RULE_ICON_OPTIONS: { key: string; label: string; icon: string }[] = [
  { key: "calendar_check", label: "Calendario", icon: "pi pi-calendar-plus" },
  { key: "spark", label: "Rayo", icon: "pi pi-bolt" },
  { key: "trophy", label: "Trofeo", icon: "pi pi-trophy" },
  { key: "gift", label: "Regalo", icon: "pi pi-gift" },
  { key: "alert", label: "Alerta", icon: "pi pi-exclamation-triangle" },
  { key: "user_check", label: "Perfil", icon: "pi pi-user" },
  { key: "share", label: "Compartir", icon: "pi pi-share-alt" },
  { key: "share_event", label: "Compartir evento", icon: "pi pi-send" },
  { key: "lightbulb", label: "Idea", icon: "pi pi-lightbulb" },
  // Íconos de las reglas de racha que siembra el backend. PrimeIcons no tiene fuego.
  { key: "flame", label: "Racha", icon: "pi pi-sparkles" },
  { key: "flame_double", label: "Racha larga", icon: "pi pi-star-fill" },
];

const DEFAULT_POINT_RULE_ICON_CLASS = "pi pi-bolt";

export const pointRuleIconClass = (iconKey: string | null): string =>
  POINT_RULE_ICON_OPTIONS.find((option) => option.key === iconKey)?.icon ??
  DEFAULT_POINT_RULE_ICON_CLASS;

export const canEditPointRuleField = (rule: PointRule, field: PointRuleEditableField): boolean =>
  rule.editableFields.includes(field);

// Signo menos tipográfico (U+2212), no el guion.
const MINUS_SIGN = "−";

/** Cómo se nombran los puntos en un texto: el BO dice "puntos"; la vista previa, el nombre del tenant. */
export type PointUnitName = { singular: string; plural: string };

const BACKOFFICE_POINT_UNIT: PointUnitName = { singular: "punto", plural: "puntos" };

export const formatPointRuleValue = (
  rule: PointRule,
  unitName: PointUnitName = BACKOFFICE_POINT_UNIT
): string => {
  const unitFor = (amount: number): string =>
    amount === 1 ? unitName.singular : unitName.plural;
  if (rule.ruleKind === "entity_amount") {
    if (rule.pointsValue === null) return "Definido en cada evento";
    return `Hasta ${rule.pointsValue} ${unitFor(rule.pointsValue)} por evento`;
  }
  if (rule.pointsValue === null) {
    return "Sin configurar";
  }
  const unit = unitFor(rule.pointsValue);
  return rule.isPenalty
    ? `${MINUS_SIGN}${rule.pointsValue} ${unit}`
    : `+${rule.pointsValue} ${unit}`;
};

/**
 * El valor tal como lo muestra la fila "Cómo ganar" de la app: solo el número con signo
 * ("+10", "−5"), sin la unidad. Para los montos variables no hay un número fijo que mostrar,
 * así que se conserva la leyenda. Es lo que la vista previa pasa a `PointRulePreview`.
 */
export const formatPointRuleValueCompact = (rule: PointRule): string => {
  if (rule.ruleKind === "entity_amount") {
    return rule.pointsValue === null ? "Definido en cada evento" : `Hasta ${rule.pointsValue}`;
  }
  if (rule.pointsValue === null) {
    return "Sin configurar";
  }
  return rule.isPenalty ? `${MINUS_SIGN}${rule.pointsValue}` : `+${rule.pointsValue}`;
};

/** "3 actividades (solo gratuitas)": el umbral sin el período. */
export const formatPointRuleThresholdCount = (rule: PointRule): string => {
  const unit = rule.thresholdCount === 1 ? "actividad" : "actividades";
  const scope = rule.freeEventsOnly ? " (solo gratuitas)" : "";
  return `${rule.thresholdCount} ${unit}${scope}`;
};

/** Una racha cuenta semanas o meses según el período de su regla base. */
export const resolvePointRuleStreakPeriod = (
  rule: Pick<PointRule, "baseRuleId">,
  rules: readonly PointRule[]
): PointWindowPeriod | null =>
  rule.baseRuleId === null
    ? null
    : rules.find((candidate) => candidate.id === rule.baseRuleId)?.windowPeriod ?? null;

/**
 * "A las 6 semanas seguidas": cuándo paga un hito de racha. Un hito viejo con 0 semanas no paga.
 */
export const formatPointRuleStreakCadence = (
  streakPeriods: number,
  basePeriod: PointWindowPeriod | null
): string => {
  const periods = streakPeriods;
  if (periods < 1) return "Sin semanas: no paga";
  if (basePeriod === "month") {
    return periods === 1 ? "Al primer mes de racha" : `A los ${periods} meses seguidos`;
  }
  return periods === 1 ? "A la primera semana de racha" : `A las ${periods} semanas seguidas`;
};

export const formatPointRuleCondition = (
  rule: PointRule,
  streakBasePeriod: PointWindowPeriod | null = null
): string => {
  if (rule.ruleKind === "threshold_window") {
    const unit = rule.thresholdCount === 1 ? "actividad" : "actividades";
    const scope = rule.freeEventsOnly ? " (solo gratuitas)" : "";
    if (rule.windowPeriod === "custom" && rule.windowStartsOn && rule.windowEndsOn) {
      const range = `entre el ${formatDayKey(rule.windowStartsOn)} y el ${formatDayKey(rule.windowEndsOn)}`;
      return `${rule.thresholdCount} ${unit} ${range}${scope}`;
    }
    const period = rule.windowPeriod === "month" ? "mes" : "semana";
    return `${rule.thresholdCount} ${unit} por ${period}${scope}`;
  }
  if (rule.ruleKind === "streak") {
    return formatPointRuleStreakCadence(rule.streakPeriods, streakBasePeriod);
  }
  return "—";
};
